import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

// Configuration for shift changes
const SHIFT_CHANGE_CONFIG = {
  morningHour: 6,  // 6 AM
  eveningHour: 18, // 6 PM
  checkIntervalMs: 300000, // Check every 5 minutes (increased from 30 seconds)
  gracePeriodMs: 60000,   // 1 minute grace period
  debugMode: false        // Enable for additional console logging
};

/**
 * Non-rendering component that monitors for shift changes and logs out staff users
 * at 6am and 6pm automatically for security purposes
 */
const ShiftChangeMonitor = () => {
  const { user, hasRole, logout } = useAuth();
  
  useEffect(() => {
    // Only monitor for staff users
    const isStaffUser = user && (hasRole('ADMIN') || hasRole('MANAGEMENT') || hasRole('OPERATOR'));
    
    if (!isStaffUser) {
      if (SHIFT_CHANGE_CONFIG.debugMode) {
        // console.log('[ShiftChangeMonitor] Not a staff user, monitoring disabled');
      }
      return;
    }
    
    if (SHIFT_CHANGE_CONFIG.debugMode) {
      // console.log('[ShiftChangeMonitor] Staff user detected, monitoring shift changes');
    }
    
    // Function to check if we're within the shift change period
    const checkForShiftChange = () => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      
      // Skip checks if not close to shift change time (optimize performance)
      // Only check if we're within 10 minutes of a shift change
      const isCloseToMorningShift = Math.abs(currentHour - SHIFT_CHANGE_CONFIG.morningHour) <= 1;
      const isCloseToEveningShift = Math.abs(currentHour - SHIFT_CHANGE_CONFIG.eveningHour) <= 1;
      
      if (!isCloseToMorningShift && !isCloseToEveningShift) {
        if (SHIFT_CHANGE_CONFIG.debugMode) {
          // console.log('[ShiftChangeMonitor] Not close to shift change time, skipping check');
        }
        return;
      }
      
      // Calculate time until next shift change for debugging
      if (SHIFT_CHANGE_CONFIG.debugMode) {
        const nextShiftHour = 
          currentHour < SHIFT_CHANGE_CONFIG.morningHour 
            ? SHIFT_CHANGE_CONFIG.morningHour 
            : currentHour < SHIFT_CHANGE_CONFIG.eveningHour 
              ? SHIFT_CHANGE_CONFIG.eveningHour 
              : SHIFT_CHANGE_CONFIG.morningHour + 24;
        
        let hoursUntilShift = nextShiftHour - currentHour;
        if (hoursUntilShift > 23) hoursUntilShift -= 24;
        
        const minutesUntilShift = 60 - currentMinute;
        // console.log(`[ShiftChangeMonitor] Next shift change in ~${hoursUntilShift}h ${minutesUntilShift}m`);
      }
      
      // Check if current time is at a shift change (with grace period)
      if (
        (currentHour === SHIFT_CHANGE_CONFIG.morningHour && currentMinute < SHIFT_CHANGE_CONFIG.gracePeriodMs / 60000) ||
        (currentHour === SHIFT_CHANGE_CONFIG.eveningHour && currentMinute < SHIFT_CHANGE_CONFIG.gracePeriodMs / 60000)
      ) {
        // Show alert to user before logging them out
        window.alert(`Shift change detected! For security reasons, you'll be automatically logged out in a few seconds.`);
        
        // console.log('[ShiftChangeMonitor] Shift change detected, logging out staff user');
        
        // Logout with a slight delay to allow the alert to be seen
        setTimeout(() => {
          logout();
        }, 3000);
      }
    };
    
    // Run the check immediately but wait a bit to let other components load first
    const initialCheckTimeout = setTimeout(() => {
      checkForShiftChange();
    }, 5000);
    
    // Set up interval to check regularly
    const intervalId = setInterval(checkForShiftChange, SHIFT_CHANGE_CONFIG.checkIntervalMs);
    
    // Clean up on unmount
    return () => {
      clearTimeout(initialCheckTimeout);
      clearInterval(intervalId);
      if (SHIFT_CHANGE_CONFIG.debugMode) {
        // console.log('[ShiftChangeMonitor] Cleaning up shift change monitor');
      }
    };
  }, [user, hasRole, logout]);
  
  // This is a non-rendering component
  return null;
};

export default ShiftChangeMonitor; 